/**
 * System Status List — severity-grouped system list with collapsible sections.
 * Groups: Critical → Watch → Offline → Online (collapsed by default).
 * Tier 2 component (expanded by default).
 */

import { useState, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { t } from '@/lib/vocabulary';
import { useApp } from '@/contexts/AppContext';
import { FleetSystemRow } from './FleetSystemRow';
import type { FleetSystemWithSeverity, FleetSummaryCounts } from '@/lib/fleet-severity';
import { sortCriticalByUrgency, sortBySeverity } from '@/lib/fleet-severity';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type SortKey = 'severity' | 'name' | 'soc';
type FilterKey = 'all' | 'critical' | 'watch' | 'offline' | 'stable';

interface SystemStatusListProps {
  systems: FleetSystemWithSeverity[];
  counts: FleetSummaryCounts;
  rowActions: {
    onHide: (propertyId: string) => void;
    onRemove: (id: string, name: string) => void;
    onEditTags: (system: FleetSystemWithSeverity) => () => void;
  };
  initialFilter?: FilterKey;
}

interface SeverityGroupProps {
  label: string;
  systems: FleetSystemWithSeverity[];
  defaultExpanded: boolean;
  minimal?: boolean;
  rowActions: SystemStatusListProps['rowActions'];
}

function SeverityGroup({ label, systems, defaultExpanded, minimal, rowActions }: SeverityGroupProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (systems.length === 0) return null;

  return (
    <section aria-label={`${label} systems`}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-2 rounded-lg border border-border/50 bg-wt-bg-tertiary/50 px-3 py-2 text-left hover:bg-wt-bg-tertiary/80 transition-colors"
      >
        <ChevronDown
          className={cn(
            'h-4 w-4 text-wt-text-muted shrink-0 transition-transform duration-200',
            !expanded && '-rotate-90'
          )}
        />
        <span className="text-sm font-semibold uppercase tracking-wider text-wt-text-secondary">
          {label} ({systems.length})
        </span>
      </button>
      <div
        className="grid transition-[grid-template-rows] duration-200 ease-out"
        style={{ gridTemplateRows: expanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className={cn('mt-2', minimal ? 'space-y-1.5 pl-2' : 'space-y-2')}>
            {systems.map((s) => (
              <FleetSystemRow
                key={s.id}
                system={s}
                minimal={minimal}
                onHide={rowActions.onHide}
                onRemove={rowActions.onRemove}
                onEditTags={rowActions.onEditTags(s)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function SystemStatusList({ systems, counts, rowActions, initialFilter }: SystemStatusListProps) {
  const { isHomeowner } = useApp();
  const [filter, setFilter] = useState<FilterKey>(initialFilter ?? 'all');
  const [sortKey, setSortKey] = useState<SortKey>('severity');

  const critical = useMemo(
    () => sortCriticalByUrgency(systems.filter((s) => s.severity === 'critical')),
    [systems]
  );
  const watch = useMemo(
    () => sortBySeverity(systems.filter((s) => s.severity === 'watch'), sortKey),
    [systems, sortKey]
  );
  const offline = useMemo(
    () => sortBySeverity(systems.filter((s) => s.severity === 'offline'), sortKey),
    [systems, sortKey]
  );
  const stable = useMemo(
    () => sortBySeverity(systems.filter((s) => s.severity === 'stable'), sortKey),
    [systems, sortKey]
  );

  const nonHealthyCount = counts.critical + counts.watch + counts.offline;
  const collapsedSummaryText = nonHealthyCount > 0
    ? `${counts.critical} critical \u00b7 ${counts.watch} watch \u00b7 ${counts.offline} offline`
    : 'All systems healthy';

  const filterPills: { key: FilterKey; label: string; count?: number }[] = [
    { key: 'all', label: 'All' },
    { key: 'critical', label: t('critical', isHomeowner), count: counts.critical },
    { key: 'watch', label: t('watch', isHomeowner), count: counts.watch },
    { key: 'offline', label: t('offline', isHomeowner), count: counts.offline },
    { key: 'stable', label: t('stable', isHomeowner), count: counts.stable },
  ];

  return (
    <div className="space-y-3">
      {/* Filter pills + sort */}
      <div className="flex flex-wrap items-center gap-2">
        {filterPills.map((pill) => (
          <Button
            key={pill.key}
            variant={filter === pill.key ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 text-xs"
            onClick={() => setFilter(pill.key)}
          >
            {pill.label}
            {pill.count != null && pill.count > 0 && (
              <span className="ml-1 font-mono text-muted-foreground">{pill.count}</span>
            )}
          </Button>
        ))}
        <div className="ml-auto">
          <Select value={sortKey} onValueChange={(v) => setSortKey(v as SortKey)}>
            <SelectTrigger className="w-[120px] bg-wt-bg-primary h-7 text-xs">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="severity">Severity</SelectItem>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="soc">SOC</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Grouped lists */}
      {filter === 'all' ? (
        <div className="space-y-3">
          <SeverityGroup label={t('critical', isHomeowner)} systems={critical} defaultExpanded={true} rowActions={rowActions} />
          <SeverityGroup label={t('watch', isHomeowner)} systems={watch} defaultExpanded={true} rowActions={rowActions} />
          <SeverityGroup label={t('offline', isHomeowner)} systems={offline} defaultExpanded={true} rowActions={rowActions} />
          <SeverityGroup label={t('stable', isHomeowner)} systems={stable} defaultExpanded={false} minimal rowActions={rowActions} />
        </div>
      ) : (
        <div className="space-y-2">
          {(filter === 'critical' ? critical
            : filter === 'watch' ? watch
            : filter === 'offline' ? offline
            : stable
          ).map((s) => (
            <FleetSystemRow
              key={s.id}
              system={s}
              minimal={filter === 'stable'}
              onHide={rowActions.onHide}
              onRemove={rowActions.onRemove}
              onEditTags={rowActions.onEditTags(s)}
            />
          ))}
          {(filter === 'critical' ? critical
            : filter === 'watch' ? watch
            : filter === 'offline' ? offline
            : stable
          ).length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No {filter} systems.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export { type FilterKey as SystemStatusFilterKey };
